// extract.ts
// The one place that knows the CMS field slugs behind the view model.

import { Catalog, CatalogAsset, CatalogElevation, CatalogItem, isAsset } from '../../../../shared/utils/elevation-catalog';

/** The values the outputs render, extracted from the catalog's rows. */
export interface ElevationView {
  /** The CMS display name — the name without the roofline — else the name. */
  displayName: string;
  beds: string;
  baths: string;
  sqFeet: string;
  heroImage: CatalogAsset | null;
}

const text = (value: unknown): string => (value == null ? '' : String(value));

/** The home plan an elevation points to, from the catalog's table. */
export const planOf = (catalog: Catalog, elevation: CatalogElevation): CatalogItem | null =>
  (elevation.plan && catalog.homePlans.find((plan) => plan.id === elevation.plan!.id)) || null;

/** Style slug to alias, from the catalog's styles table. */
export const styleAliases = (catalog: Catalog): Record<string, string> =>
  Object.fromEntries(catalog.styles.map((style) => [style.slug, text(style.fields.alias)]));

/** The plan a duplex reads its missing values from. */
const donorOf = (catalog: Catalog, plan: CatalogItem | null): CatalogItem | null => {
  const id = plan?.fields['duplex-single'];

  return (typeof id === 'string' && catalog.homePlans.find((p) => p.id === id)) || null;
};

/** Beds and baths read from the plan; sq feet elevation-first. */
export const extract = (catalog: Catalog, elevation: CatalogElevation): ElevationView => {
  const plan = planOf(catalog, elevation);
  const donor = donorOf(catalog, plan);

  return {
    displayName: text(elevation.fields['display-name']) || elevation.name,
    beds: text(plan?.fields['beds-2'] ?? donor?.fields['beds-2']),
    baths: text(plan?.fields['baths-2'] ?? donor?.fields['baths-2']),
    sqFeet: text(elevation.fields['sq-feet'] ?? plan?.fields['square-footage-2']),
    heroImage: isAsset(elevation.fields['hero-image']) ? elevation.fields['hero-image'] : null
  };
};
